import {
    List,
    Avatar,
    Typography,
    Box,
    Pagination,
    Paper,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCallHistory } from "../Redux/Actions/CallAction";
import { GoClockFill } from "react-icons/go";
import useTitle from "../hooks/useTitle";
import { dayjs, userTimeZone } from "../utils/dayjsUtils";
import CallDetailsModal from "../Models/CallDetailsModal";
import SearchField from "../context/SearchField";
import Loader from "../components/Loader/Loading";
import notfound from "../assets/images/not-found.png";

const CallHistory = () => {
    useTitle("Call History");
    const dispatch = useDispatch();
    const { Call } = useSelector((state) => state);
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [callmodal, setcallmodal] = useState(false);
    const [selectedCall, setSelectedCall] = useState(null);
    const debounceTimeout = useRef(null);
    const rowsPerPage = 10;

    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            setDebouncedQuery(query);
        }, 500);
        return () => clearTimeout(debounceTimeout.current);
    }, [query]);

    useEffect(() => {
        setPage(1);
    }, [debouncedQuery]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await dispatch(getCallHistory(debouncedQuery, page, rowsPerPage));
                setData(res?.data || []);
                setTotalPages(res?.totalPages || 1);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [dispatch, page, debouncedQuery]);

    const handlecalldetail = (scheduleId) => {
        setSelectedCall(scheduleId);
        setcallmodal(true);
    };

    const formatTime = (utcDateTime) => {
        return dayjs(utcDateTime).tz(userTimeZone).format("DD-MM-YYYY hh:mm A");
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    return (
        <>
            <Box sx={{ padding: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", md: "center" },
                        mb: 2,
                        gap: 1,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#424242" }}
                    >
                        Call History
                    </Typography>

                    <SearchField search={query} setSearch={setQuery} />
                </Box>

                <Paper sx={{ minHeight: "78vh", border: 'none', boxShadow: 'none', bgcolor: 'transparent', display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <Box>
                        {Call.loading && !data.length ? (
                            <div className="flex justify-center items-center h-[60vh]">
                                <Loader />
                            </div>
                        ) : (
                            <List
                                dense
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                {data.map((call) => (
                                    <React.Fragment key={call.id}>
                                        <div
                                            className="flex justify-between items-center flex-col md:flex-row gap-4 cursor-pointer"
                                            onClick={() => handlecalldetail(call.id)}
                                        >
                                            <div className="flex items-center gap-3 w-full">
                                                <Avatar
                                                    src={call?.Receiver?.profileImage}
                                                    alt=""
                                                    className="object-cover rounded-full !h-[40px] !w-[40px]"
                                                />
                                                <div className="flex flex-col gap-1">
                                                    <p className="font-[600] text-[#000000] text-[14px]">
                                                        {call.Receiver?.first_name + " " + call.Receiver?.last_name}
                                                    </p>
                                                    <p className="text-[#000000] text-[14px] font-[500] flex items-center gap-1">
                                                        <span className="text-[15px] text-gray-600">
                                                            <GoClockFill />
                                                        </span>
                                                        {call.duration} min
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-3 w-full justify-end">
                                                <span className="text-[#000000] text-[14px] font-[500]">
                                                    {formatTime(call.startTime)}
                                                </span>
                                            </div>
                                        </div>
                                        <hr className="text-[#EDEDED]" />
                                    </React.Fragment>
                                ))}
                            </List>
                        )}

                        {!Call.loading && !data.length && (
                            <div className="flex flex-col justify-center items-center w-full h-[60vh]">
                                <img
                                    src={notfound}
                                    alt="Not Found"
                                    style={{ width: "200px", height: "auto" }}
                                />
                                <span className="text-xl font-bold mt-6 text-gray-400">
                                    No call history found.
                                </span>
                            </div>
                        )}
                    </Box>

                    <Box className="flex justify-end pt-3">
                        <Pagination
                            shape="rounded"
                            count={Math.max(totalPages, 1)}
                            page={page}
                            onChange={handlePageChange}
                            sx={{
                                "& .MuiPaginationItem-root.Mui-selected": {
                                    background:
                                        "linear-gradient(to right, #EF5A24 1%, #F38157 99%)",
                                    color: "#fff",
                                },
                                "@media (max-width: 960px)": {
                                    fontSize: "0.75rem",
                                    "& .MuiPaginationItem-root": {
                                        minWidth: "auto",
                                        padding: "8px",
                                        margin: "2px",
                                    },
                                },
                                "@media (max-width: 600px)": {
                                    fontSize: "0.5rem",
                                    "& .MuiPaginationItem-root": {
                                        padding: "8px",
                                        margin: "1px",
                                    },
                                },
                            }}
                        />
                    </Box>
                </Paper>
            </Box>

            <CallDetailsModal
                open={callmodal}
                setOpen={setcallmodal}
                scheduleId={selectedCall}
            />
        </>
    );
};

export default CallHistory;
